import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { MeetingType } from '../../types/meetingType';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { FormInput } from '../ui/FormInput';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmContext';

export default function MeetingTypeSettings() {
  const confirm = useConfirm();
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<MeetingType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const fetchMeetingTypes = async () => {
    const q = query(collection(db, 'meetingTypes'), orderBy('name'));
    const querySnapshot = await getDocs(q);
    const types = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as MeetingType[];
    setMeetingTypes(types);
  };

  useEffect(() => {
    fetchMeetingTypes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingType?.id) {
        const typeRef = doc(db, 'meetingTypes', editingType.id);
        await updateDoc(typeRef, {
          ...formData,
          updatedAt: new Date()
        });
      } else {
        await addDoc(collection(db, 'meetingTypes'), {
          ...formData,
          createdAt: new Date()
        });
      }
      
      setShowForm(false);
      setEditingType(null);
      setFormData({ name: '', description: '' });
      await fetchMeetingTypes();
    } catch (error) {
      console.error('Error saving meeting type:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (type: MeetingType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (typeId: string) => {
    const ok = await confirm({ title: 'Supprimer le type de rencontre', description: 'Êtes-vous sûr de vouloir supprimer ce type de rencontre ? Les rapports associés ne seront pas affectés.', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (ok) {
      try {
        await deleteDoc(doc(db, 'meetingTypes', typeId));
        await fetchMeetingTypes();
      } catch (error) {
        console.error('Error deleting meeting type:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Types de Rencontre</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingType(null);
            setFormData({ name: '', description: '' });
          }}
          className="inline-flex items-center px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          {showForm ? (
            <>
              <X size={20} className="mr-2" />
              Fermer
            </>
          ) : (
            <>
              <Plus size={20} className="mr-2" />
              Nouveau type
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput
              label="Nom du type de rencontre"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingType(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
              >
                {editingType ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {meetingTypes.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">{type.name}</td>
                <td className="px-6 py-4">{type.description || '-'}</td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => type.id && handleDelete(type.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}